import {
    EmailTemplateSavePayload,
    fetchEmailEditorConfigThunk,
    fetchEmailTemplateThunk,
    resetEmailEditor,
    saveEmailTemplateThunk,
    updateEmailTemplateThunk
} from '@spike/email-marketing-action';
import { EmailTemplate, EmailTemplateCategory, MergeTag } from '@spike/email-marketing-model';
import { ConfirmDialog } from 'components/UI';
import Button from 'components/UI/V3/Button/Button';
import { InlineEditableTitle } from 'components/UI/V3/Forms/InlineEditableTitle';
import { FullWindowModal, FullWindowModalBody, FullWindowModalHeader } from 'components/UI/V3/Modals/FullWindowModal';
import Spinner from 'components/UI/V3/Spinner/Spinner';
import { useApiClientWrapper } from 'hooks';
import moment from 'moment';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import EmailEditor, { EditorRef, EmailEditorProps } from 'react-email-editor';

// Derived from the SDK's signature so we don't need to know the exact export
// name of the design type (`JSONTemplate<"email">` in current versions of
// `react-email-editor`). This stays pinned to whatever the installed SDK
// expects, even if Unlayer renames it later.
type UnlayerDesign = Parameters<NonNullable<EditorRef['editor']>['loadDesign']>[0];
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { MarketingEmailsPath } from 'routes/Paths';
import { RootState } from 'store';
import styles from './EmailTemplateEditorComponent.module.scss';

interface RouteParams {
    id?: string;
}

/**
 * Convert merge tags from the lib's array shape into Unlayer's expected
 * keyed-object shape: { first_name: { name, value }, ... }.
 */
const buildUnlayerMergeTags = (mergeTags: MergeTag[]) => {
    return mergeTags.reduce<Record<string, { name: string; value: string }>>((acc, tag) => {
        const key = tag.value.replace(/[{}]/g, '').trim();
        if (key) {
            acc[key] = { name: tag.name, value: tag.value };
        }
        return acc;
    }, {});
};

/**
 * Placeholder category persisted with every marketplace template. Product
 * removed the category picker from the editor, but the backend column is
 * still required (`EmailTemplateSavePayload.category`). On edit we preserve
 * whatever value the template already had; on create we default to
 * `'engagement'`. Cleanup follow-up: BE makes the column nullable, lib
 * relaxes the type, FE stops sending this.
 */
const DEFAULT_CATEGORY: EmailTemplateCategory = 'engagement';

/**
 * Format used for the auto-generated title on new marketplace templates.
 * Matches the Automation module's reference (`Template - June 11, 2026,
 * 8:12 PM`).
 */
const DEFAULT_NAME_FORMAT = 'MMMM D, YYYY, h:mm A';

/**
 * Optional props enable EMBEDDED mode — used by the campaign wizard so the
 * editor can be rendered on top of the wizard (the editor is `position: fixed`
 * and already overlays the app) without taking over routing.
 *
 *   Standalone mode (route — `/templates/new` or `/templates/:id/edit`):
 *     All three props are omitted. The component reads `:id` from URL params,
 *     and `history.push`-es back to the templates tab on save/exit.
 *
 *   Embedded mode (wizard overlay):
 *     - `embeddedTemplateId` overrides the URL param. Leave undefined to create
 *       a new template, set a number to edit an existing one.
 *     - `onSave(template)` is called after a successful save/update instead of
 *       navigating. Parent typically uses the returned template to auto-select
 *       it in the wizard and close the overlay.
 *     - `onExit()` is called on cancel / discard / unconfigured-error instead
 *       of navigating. Parent closes the overlay.
 *
 * The two modes are exclusive in practice; mixing (e.g. supplying `onSave` but
 * not `onExit`) is allowed but unusual. The component falls back to navigation
 * for any callback the caller did not provide.
 */
interface EmailTemplateEditorComponentProps {
    embeddedTemplateId?: number;
    onSave?: (template: EmailTemplate) => void;
    onExit?: () => void;
}

const EmailTemplateEditorComponent: FunctionComponent<EmailTemplateEditorComponentProps> = ({
    embeddedTemplateId,
    onSave,
    onExit
}) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const apiClientWrapper = useApiClientWrapper();
    const { id: idParam } = useParams<RouteParams>();
    const templateId = embeddedTemplateId !== undefined ? embeddedTemplateId : idParam ? Number(idParam) : undefined;
    const isEditMode = typeof templateId === 'number' && !Number.isNaN(templateId);

    /**
     * Single exit point — routes through the parent's `onExit` when embedded,
     * falls back to URL navigation in standalone mode.
     */
    const exitEditor = () => {
        if (onExit) {
            onExit();
        } else {
            history.push(`${MarketingEmailsPath}?tab=templates`);
        }
    };

    const unlayerConfig = useSelector((state: RootState) => state.emailTemplates.unlayerConfig);
    const currentTemplate = useSelector((state: RootState) => state.emailTemplates.currentTemplate);
    const saving = useSelector((state: RootState) => state.emailTemplates.saving);

    const editorRef = useRef<EditorRef | null>(null);

    const [name, setName] = useState<string>('');
    const [nameError, setNameError] = useState<boolean>(false);
    const [editorReady, setEditorReady] = useState<boolean>(false);
    const [dirty, setDirty] = useState<boolean>(false);
    const [confirmExitOpen, setConfirmExitOpen] = useState<boolean>(false);
    const [prefilled, setPrefilled] = useState<boolean>(false);

    // ---------------------------------------------------------------------
    // Initial loading: editor config + template (if edit mode)
    // ---------------------------------------------------------------------
    useEffect(() => {
        if (!unlayerConfig) {
            dispatch(fetchEmailEditorConfigThunk(apiClientWrapper));
        }
        if (isEditMode && templateId !== undefined) {
            dispatch(fetchEmailTemplateThunk(apiClientWrapper, templateId));
        }
        return () => {
            dispatch(resetEmailEditor());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Pre-fill the inline title from the loaded template (edit mode only).
    useEffect(() => {
        if (isEditMode && currentTemplate && !prefilled) {
            setName(currentTemplate.name);
            setPrefilled(true);
        }
    }, [isEditMode, currentTemplate, prefilled]);

    // Auto-generate a default title for new templates so the user lands in
    // the editor with something already filled (matches the Automation
    // module's UX). Fires once on mount; user can edit via the inline pencil.
    useEffect(() => {
        if (!isEditMode && !name) {
            setName(`Template - ${moment().format(DEFAULT_NAME_FORMAT)}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Once both editor is ready AND template (if any) is loaded, push the design.
    useEffect(() => {
        if (!editorReady) return;
        if (isEditMode && currentTemplate?.designJson && editorRef.current?.editor) {
            try {
                editorRef.current.editor.loadDesign(currentTemplate.designJson as unknown as UnlayerDesign);
            } catch (e) {
                console.error('[EmailTemplateEditor] Failed to load design', e);
            }
        }
    }, [editorReady, isEditMode, currentTemplate]);

    const unlayerMergeTags = useMemo(
        () => (unlayerConfig ? buildUnlayerMergeTags(unlayerConfig.mergeTags) : {}),
        [unlayerConfig]
    );

    const editorOptions = useMemo<EmailEditorProps['options']>(() => {
        if (!unlayerConfig) return {};
        return {
            user: unlayerConfig.userId
                ? {
                      id: unlayerConfig.userId,
                      ...(unlayerConfig.signature ? { signature: unlayerConfig.signature } : {})
                  }
                : undefined,
            mergeTags: unlayerMergeTags
        };
    }, [unlayerConfig, unlayerMergeTags]);

    // ---------------------------------------------------------------------
    // Handlers
    // ---------------------------------------------------------------------
    const handleEditorReady: EmailEditorProps['onReady'] = () => {
        setEditorReady(true);
    };

    // Mark form as dirty when user changes something. Unlayer doesn't expose a
    // direct dirty signal — we approximate by tracking name edits and assume
    // any session in the editor is potentially dirty after first ready.
    useEffect(() => {
        if (editorReady) setDirty(true);
    }, [name, editorReady]);

    const handleNameChange = (next: string) => {
        setName(next);
        // Any successful commit clears a previous "required" error.
        setNameError(false);
    };

    const handleSaveClick = () => {
        if (!name.trim()) {
            // `InlineEditableTitle` auto-opens its edit input when `error`
            // becomes truthy, so the user lands right where they need to fix.
            setNameError(true);
            return;
        }

        if (!editorRef.current?.editor) {
            console.error('[EmailTemplateEditor] Editor not ready when Save clicked');
            return;
        }

        editorRef.current.editor.exportHtml(async data => {
            const payload: EmailTemplateSavePayload = {
                name: name.trim(),
                html: data.html,
                designJson: data.design as object,
                // Preserve the existing category on edit; default to
                // 'engagement' on create. See DEFAULT_CATEGORY for why we
                // still need to send it.
                category: currentTemplate?.category ?? DEFAULT_CATEGORY
            };

            const result =
                isEditMode && templateId !== undefined
                    ? await dispatch(updateEmailTemplateThunk(apiClientWrapper, templateId, payload) as any)
                    : await dispatch(saveEmailTemplateThunk(apiClientWrapper, payload) as any);

            if (result) {
                if (onSave) {
                    onSave(result);
                } else {
                    history.push(`${MarketingEmailsPath}?tab=templates`);
                }
            }
        });
    };

    const handleExitClick = () => {
        if (dirty) {
            setConfirmExitOpen(true);
        } else {
            exitEditor();
        }
    };

    const handleConfirmExit = () => {
        setConfirmExitOpen(false);
        exitEditor();
    };

    // ---------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------

    // Editor config not configured (project_id missing on backend).
    if (unlayerConfig && !unlayerConfig.projectId) {
        return (
            <FullWindowModal>
                <FullWindowModalBody>
                    <div className={styles.errorBox}>
                        <h2>Email editor is not configured</h2>
                        <p>Please contact support to enable email template editing.</p>
                        <Button
                            label="Back to templates"
                            variant="primary"
                            onClick={exitEditor}
                        />
                    </div>
                </FullWindowModalBody>
            </FullWindowModal>
        );
    }

    // Initial load.
    if (!unlayerConfig || (isEditMode && !currentTemplate)) {
        return (
            <FullWindowModal>
                <FullWindowModalBody>
                    <div className={styles.loaderContainer}>
                        <Spinner />
                    </div>
                </FullWindowModalBody>
            </FullWindowModal>
        );
    }

    return (
        <FullWindowModal>
            <FullWindowModalHeader>
                <div className={styles.headerLayout}>
                    <div className={styles.topBarLeft}>
                        <InlineEditableTitle
                            value={name}
                            onChange={handleNameChange}
                            error={nameError ? 'Required' : null}
                            maxLength={100}
                            ariaLabel="Template name"
                        />
                    </div>

                    <div className={styles.topBarRight}>
                        <Button
                            label="Cancel"
                            variant="outlined"
                            onClick={handleExitClick}
                            disabled={saving}
                        />
                        <Button
                            label="Save & Exit"
                            variant="primary"
                            onClick={handleSaveClick}
                            isLoading={saving}
                            disabled={saving}
                        />
                    </div>
                </div>
            </FullWindowModalHeader>

            <FullWindowModalBody>
                <EmailEditor
                    ref={editorRef}
                    onReady={handleEditorReady}
                    projectId={unlayerConfig.projectId ?? undefined}
                    options={editorOptions}
                    minHeight="100%"
                    style={{ flex: 1 }}
                />
            </FullWindowModalBody>

            <ConfirmDialog
                open={confirmExitOpen}
                title="Discard changes?"
                question="Your unsaved changes will be lost."
                cancelButtonLabel="Keep editing"
                confirmButtonLabel="Discard"
                onCancel={() => setConfirmExitOpen(false)}
                onConfirm={handleConfirmExit}
            />
        </FullWindowModal>
    );
};

export default EmailTemplateEditorComponent;
